import React, { useState, useEffect } from 'react';
import {
  FiTarget as TargetIcon,
  FiAlertTriangle as AlertIcon,
  FiUsers as UsersIcon,
  FiGitBranch as BranchIcon,
  FiShield as ShieldIcon,
} from 'react-icons/fi';
import { impactAPI, catalogAPI } from '../../../services/dataopsApi';
import { useProject } from '../../../contexts/ProjectContext';
import { useFilter } from '../../../contexts/FilterContext';

interface Dataset {
  id: string;
  name: string;
  [key: string]: unknown;
}

interface ImpactOverview {
  downstream_jobs: number;
  downstream_datasets: number;
  affected_users: number;
  critical_paths: number;
  impact_level: string;
}

interface Dependency {
  id: string;
  name: string;
  dependency_type: string;
  criticality: string;
  users_count: number;
}

interface ImpactAssessment {
  overview: ImpactOverview;
  dependencies: Dependency[];
  message?: string;
}

type Criticality = 'CRITICAL' | 'HIGH' | 'MEDIUM' | 'LOW';

function getCriticalityColor(criticality: string): string {
  switch (criticality as Criticality) {
    case 'CRITICAL':
      return 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-300';
    case 'HIGH':
      return 'bg-orange-100 text-orange-800 dark:bg-orange-900/20 dark:text-orange-300';
    case 'MEDIUM':
      return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-300';
    case 'LOW':
      return 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-300';
    default:
      return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';
  }
}

const ImpactPage: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [selectedDataset, setSelectedDataset] = useState<string | null>(null);
  const [assessment, setAssessment] = useState<ImpactAssessment | null>(null);
  const [datasets, setDatasets] = useState<Dataset[]>([]);
  const { selectedProjectId } = useProject();
  const { filterType, selectedId: filterId } = useFilter();

  useEffect(() => {
    setSelectedDataset(null);
    setAssessment(null);
    setDatasets([]);
    loadDatasets();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProjectId, filterType, filterId]);

  useEffect(() => {
    if (selectedDataset && datasets.length > 0) {
      loadImpactAnalysis();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDataset, datasets]);

  const loadDatasets = async () => {
    try {
      const data: Dataset[] = await catalogAPI.getDatasets();
      setDatasets(data);
      if (data.length > 0) {
        setSelectedDataset((prev) => prev || data[0].name);
      }
    } catch (err: unknown) {
      console.error('Error loading datasets:', err instanceof Error ? err.message : err);
    }
  };

  const loadImpactAnalysis = async () => {
    setLoading(true);
    try {
      const dataset = datasets.find((d) => d.name === selectedDataset) || datasets[0];
      if (dataset) {
        const data: ImpactAssessment = await impactAPI.getAnalysis({ dataset_id: dataset.id });
        setAssessment(data);
      }
    } catch (err: unknown) {
      console.error('Error loading impact analysis:', err instanceof Error ? err.message : err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center justify-center h-64">
          <div className="text-gray-500 dark:text-gray-400">Loading...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center mb-4">
          <TargetIcon className="w-12 h-12 text-blue-600 dark:text-blue-400 mr-4" />
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Impact Analysis</h1>
            <p className="mt-1 text-gray-600 dark:text-gray-400">Analyze downstream dependencies and impact</p>
          </div>
        </div>

        {/* Dataset Selector */}
        <div className="mt-6 max-w-md">
          <label htmlFor="select-dataset" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Select Dataset
          </label>
          <select id="select-dataset"
            value={selectedDataset ?? ''}
            onChange={(e) => setSelectedDataset(e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            {datasets.map((dataset) => (
              <option key={dataset.id} value={dataset.name}>
                {dataset.name}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Impact Overview Cards */}
      {assessment && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
              <div className="flex items-center">
                <div className="p-3 bg-blue-100 dark:bg-blue-900/20 rounded-lg">
                  <BranchIcon className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Downstream Jobs</p>
                  <p className="text-2xl font-bold text-gray-900 dark:text-white">
                    {assessment.overview.downstream_jobs}
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
              <div className="flex items-center">
                <div className="p-3 bg-purple-100 dark:bg-purple-900/20 rounded-lg">
                  <BranchIcon className="w-6 h-6 text-purple-600 dark:text-purple-400" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Downstream Datasets</p>
                  <p className="text-2xl font-bold text-gray-900 dark:text-white">
                    {assessment.overview.downstream_datasets}
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
              <div className="flex items-center">
                <div className="p-3 bg-green-100 dark:bg-green-900/20 rounded-lg">
                  <UsersIcon className="w-6 h-6 text-green-600 dark:text-green-400" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Affected Users</p>
                  <p className="text-2xl font-bold text-gray-900 dark:text-white">
                    {assessment.overview.affected_users}
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
              <div className="flex items-center">
                <div className="p-3 bg-orange-100 dark:bg-orange-900/20 rounded-lg">
                  <ShieldIcon className="w-6 h-6 text-orange-600 dark:text-orange-400" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Critical Paths</p>
                  <p className="text-2xl font-bold text-gray-900 dark:text-white">
                    {assessment.overview.critical_paths}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Impact Assessment Warning */}
          {assessment.overview.impact_level === 'High' && assessment.message && (
            <div className="mb-8 bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 rounded-lg p-6">
              <div className="flex items-start">
                <AlertIcon className="w-6 h-6 text-orange-600 dark:text-orange-400 mr-3 mt-0.5" />
                <div>
                  <h3 className="text-lg font-semibold text-orange-900 dark:text-orange-200 mb-2">
                    High Impact Dataset
                  </h3>
                  <p className="text-sm text-orange-800 dark:text-orange-300">{assessment.message}</p>
                </div>
              </div>
            </div>
          )}

          {/* Dependencies Table */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Downstream Dependencies</h2>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-700">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Name</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Type</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Criticality</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Users</th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                  {assessment.dependencies.map((dep) => (
                    <tr key={dep.id} className="hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                        {dep.name}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400 capitalize">
                        {dep.dependency_type}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getCriticalityColor(dep.criticality)}`}>
                          {dep.criticality}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                        {dep.users_count}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default ImpactPage;
